/**
 * This console's own record of the door overrides IT declared.
 *
 * WHY THIS IS A LOCAL STORE AND NOT A QUERY. There is no projection
 * for an active override, anywhere. The record lives in the site's
 * lifecycle broker; `rmf-door-state-mux` polls the broker directly and
 * synthesizes `DoorMode.MODE_OPEN` from it, and neither the broker nor
 * the mux republishes "this door is under override" onto any Zenoh key
 * the worker projects. The api-server cannot even reach the broker to
 * ask (NetworkPolicy -- verified live, connection refused), which is
 * why the declare/revoke verbs ride the worker in the first place. So
 * the only thing the SPA can honestly show is what it itself sent.
 *
 * WHAT THAT COSTS, said out loud because the UI has to say it too:
 *
 *   - An override declared from ANOTHER browser, from the support
 *     console, or by curl against the broker shows NOTHING here. The
 *     absence of a chip is not evidence that a door is unoverridden.
 *   - A record here means the api-server admitted the command and the
 *     worker accepted it. It does not prove the broker wrote the file,
 *     and it cannot: admission is not the outcome, the same rule the
 *     lift verbs and the task controls are built on.
 *   - The countdown is a LOCAL countdown of the GRANTED ttl (read back
 *     from `command.target.ttl_s`, which the api-server clamps and may
 *     therefore have shrunk). The mux enforces the real expiry against
 *     wall clock on its own side, so a clock skew makes this display
 *     wrong, not the override wrong.
 *
 * Persisted to `localStorage` rather than kept in memory: the TTL
 * ceiling is 30 minutes, an operator who reloads the tab mid-override
 * must not lose the revoke control, and losing it is worse than
 * showing a record whose door has since lapsed (which expires by
 * itself, in view).
 *
 * The store exposes plain functions plus a subscribe hook, matching
 * `state/user-preferences.ts` + `state/useUserPreferences.ts`.
 */

export interface DoorOverrideRecord {
  readonly site: string;
  readonly door: string;
  /** Epoch ms this console got the api-server's 2xx. */
  readonly grantedAtMs: number;
  /** The GRANTED ttl, read back off `command.target.ttl_s` -- not the
   *  one that was asked for, which the api-server may have clamped. */
  readonly ttlS: number;
  /** What was asked for, kept only so the drawer can say "clamped to
   *  30 minutes" rather than silently showing a shorter countdown than
   *  the operator chose. */
  readonly requestedTtlS: number;
  readonly commandId: string;
  /** The free-text note, if one was given. Shown back so the operator
   *  can see what they put on the record. */
  readonly note: string | null;
}

const STORAGE_PREFIX = 'nx:door-overrides';
/** Same-tab + cross-screen notification. `storage` events only fire in
 *  OTHER tabs, so a custom event is what keeps the chip in the doors
 *  table and the drawer in step within one tab. */
const CHANGE_EVENT = 'nx:door-overrides-change';

function storageKey(site: string): string {
  return `${STORAGE_PREFIX}:${site}`;
}

function isRecord(v: unknown): v is DoorOverrideRecord {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.site === 'string' &&
    typeof r.door === 'string' &&
    typeof r.grantedAtMs === 'number' &&
    Number.isFinite(r.grantedAtMs) &&
    typeof r.ttlS === 'number' &&
    Number.isFinite(r.ttlS)
  );
}

function readAll(site: string): DoorOverrideRecord[] {
  if (typeof window === 'undefined' || !site) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(site));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecord);
  } catch {
    // Private mode, a quota error, or a value some other version of
    // this code wrote. An unreadable store means "no records", never a
    // thrown render.
    return [];
  }
}

function writeAll(site: string, records: DoorOverrideRecord[]): void {
  if (typeof window === 'undefined' || !site) return;
  try {
    if (records.length === 0) window.localStorage.removeItem(storageKey(site));
    else window.localStorage.setItem(storageKey(site), JSON.stringify(records));
  } catch {
    // Ignore: the chip is a convenience, and a browser refusing
    // storage must not break the declare itself.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { site } }));
}

/**
 * Seconds left, clamped to `[0, ttlS]`.
 *
 * BOTH ends are clamped, and the upper one is not paranoia. `nowMs`
 * comes from a 1 Hz tick captured on an earlier render than the
 * `grantedAtMs` stamped in the mutation's own success callback, so
 * immediately after a declare the elapsed time is slightly NEGATIVE
 * and an unclamped `ceil` reports 181 s of a 180 s grant. A countdown
 * that starts above the TTL it was granted is a small lie about a
 * number the operator is using to decide whether the robot will make
 * it through in time.
 */
export function remainingS(record: DoorOverrideRecord, nowMs: number): number {
  const elapsed = (nowMs - record.grantedAtMs) / 1000;
  return Math.max(0, Math.min(record.ttlS, Math.ceil(record.ttlS - elapsed)));
}

/**
 * Every record for this site that has not lapsed.
 *
 * A PURE READ -- it deliberately does not prune the expired ones it
 * filters out. This runs during render (the doors table calls it for
 * every row), and a write here would dispatch the change event mid
 * render and set state in another component from inside a `useMemo`.
 * The stale rows cost nothing: every reader filters, and the next
 * declare or revoke rewrites the list without them (`writeAll` below
 * is only ever reached from an event handler).
 */
export function activeDoorOverrides(site: string, nowMs: number): DoorOverrideRecord[] {
  return readAll(site).filter((r) => remainingS(r, nowMs) > 0);
}

export function activeDoorOverride(
  site: string,
  door: string,
  nowMs: number,
): DoorOverrideRecord | null {
  return activeDoorOverrides(site, nowMs).find((r) => r.door === door) ?? null;
}

/**
 * Record a grant this console just made.
 *
 * Replaces any existing record for the same door: a second declare is
 * a fresh TTL, and the broker's write is atomic, so keeping the older
 * one would count down to an expiry that has already been superseded.
 */
export function rememberDoorOverride(record: DoorOverrideRecord): void {
  const now = Date.now();
  const others = readAll(record.site).filter(
    (r) => r.door !== record.door && remainingS(r, now) > 0,
  );
  writeAll(record.site, [...others, record]);
}

/** Forget a record -- after a revoke, or after the operator dismisses
 *  one this console can no longer vouch for. */
export function forgetDoorOverride(site: string, door: string): void {
  const now = Date.now();
  const kept = readAll(site).filter((r) => r.door !== door && remainingS(r, now) > 0);
  writeAll(site, kept);
}

/** Subscribe to same-tab changes. Returns the unsubscribe. */
export function subscribeDoorOverrides(site: string, onChange: () => void): () => void {
  function handler(e: Event) {
    const ce = e as CustomEvent<{ site: string }>;
    if (!ce.detail || ce.detail.site === site) onChange();
  }
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/**
 * The granted TTL, off a `CommandRecord.target`.
 *
 * `target` is `Record<string, unknown>` -- the api-server builds it
 * per capability and the SPA never gets a narrowed type for it -- so
 * this is the one place that reads `ttl_s` out of it. Returns null
 * when the key is missing or not a positive number, which is what an
 * api-server predating this verb answers with; the caller then falls
 * back to what it asked for and says so.
 */
export function grantedTtlS(target: Record<string, unknown> | undefined): number | null {
  const raw = target?.ttl_s;
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return null;
  return Math.floor(raw);
}
